import React, { useRef } from 'react';
import {
    MeasureDayBox,
    NotificationBox,
    MarketingBox,
    SalesPushBox,
    HundredTShirtSizeBox,
    SpecialConditionBox,
    MemoBox,
    BatchPaymentMemoBox,
    TodoCard,
} from '@icoach/memberDetail/components';
import { Card, Typography } from "@curves/ui-base";
import { useMemberDetailContext } from '@icoach/memberDetail/MemberDetailContext';
import { VALID_MEMBERSHIP_STATUSES } from "@constants/appConfig";
import useMessageDialog from "@util/hook/useMessageDialog";

const MemberDetailSideBar = ({ memberID, sourceData: memberProfile = {}, dialogRef }) => {
    const {
        resource,
        serviceTasks,
        measureDay,
        isInterviewNotMeasure,
        hasSMS,
        hasMarketing,
        proteinPush,
        probioticsPush,
        calciumPush,
        smartRingPush,
        hundredTShirtSize,
        specialCondition,
        memo,
        batchPaymentMemo,
        membershipStatus,
        discardRecordReason,
    } = memberProfile || {};
    const { serviceTodoTextOptions = [] } = resource;

    const {
        updateMeasureDay,
        updateSMSStatus,
        updateMarketingStatus,
        updateProteinPush,
        updateProbioticsPush,
        updateCalciumPush,
        updateSmartRingPush,
        updateHundredTShirtSize,
        updateSpecialCondition,
        updateMemo,
        updateBatchPaymentMemo,
        updateServiceTasks,
        updateDiscardRecordReason,
    } = useMemberDetailContext();

    const todoRef = useRef(null);

    const setMessageDialog = useMessageDialog();

    const handleChangeSMSStatus = () => {
        const { openNotificationDialog, openInactiveMemberNotificationStopDialog } = dialogRef.current || {};
        const validMember = VALID_MEMBERSHIP_STATUSES.some(item => item === membershipStatus);

        if (discardRecordReason) {
            // 無效會員不接收通知，顯示通知設定訊息
            setMessageDialog({
                open: true,
                title: "通知設定",
                msg: discardRecordReason,
            })
        } else if (validMember) {
            // 有效會員狀態時，才可以開啟通知設定彈窗
            openNotificationDialog(
                {
                    hasSMS,
                    memberID,
                },
                updateSMSStatus,
            )
        }  else {
            // 無效會員狀態時，開啟停止接收通知彈窗
            openInactiveMemberNotificationStopDialog(
                {memberID},
                ({discardRecordReason: text, hasSMS}) => {
                    updateSMSStatus(hasSMS);
                    updateDiscardRecordReason(text);
                }
            );
        }
    }

    const handleChangeMarketingStatus = () => {
        const { openMarketingDialog } = dialogRef.current || {};
        openMarketingDialog && openMarketingDialog(
            { hasMarketing, memberID },
            updateMarketingStatus,
        );
    }

    return (
        <React.Fragment>
            <Card className="px-2 py-2 mb-3">
                <Typography variant="body1" className="font-weight-bold">
                    會員狀態
                </Typography>
                <Card.CardContent>
                    <MeasureDayBox
                        sourceData={memberProfile}
                        onEdit={() =>
                            dialogRef.current &&
                            dialogRef.current.openMeasureDayDialog(
                                {
                                    measureDay,
                                    isInterviewNotMeasure,
                                    memberID,
                                },
                                updateMeasureDay,
                            )
                        }
                    />
                    <NotificationBox
                        hasSMS={hasSMS}
                        onEdit={handleChangeSMSStatus}
                    />
                    <MarketingBox
                        hasMarketing={hasMarketing}
                        onEdit={handleChangeMarketingStatus}
                    />
                    <SalesPushBox
                        label="蛋白訂單"
                        sourceData={proteinPush}
                        onEdit={() =>
                            dialogRef.current &&
                            dialogRef.current.openProteinPushDialog(
                                {
                                    proteinPush: proteinPush,
                                    memberID,
                                    ...resource,
                                },
                                updateProteinPush,
                            )
                        }
                    />
                    <SalesPushBox
                        label="益菌訂單"
                        sourceData={probioticsPush}
                        onEdit={() =>
                            dialogRef.current &&
                            dialogRef.current.openProbioticsPushDialog(
                                {
                                    probioticsPush,
                                    memberID,
                                    ...resource,
                                },
                                updateProbioticsPush,
                            )
                        }
                    />
                    <SalesPushBox
                        label="鈣訂單"
                        sourceData={calciumPush}
                        onEdit={() =>
                            dialogRef.current &&
                            dialogRef.current.openCalciumPushDialog(
                                {
                                    calciumPush,
                                    memberID,
                                    ...resource,
                                },
                                updateCalciumPush,
                            )
                        }
                    />
                    <SalesPushBox
                        label="智能系統"
                        sourceData={smartRingPush}
                        onEdit={() =>
                            dialogRef.current &&
                            dialogRef.current.openSmartRingPushDialog(
                                {
                                    smartRingPush,
                                    memberID,
                                    ...resource,
                                },
                                updateSmartRingPush,
                            )
                        }
                    />
                    <HundredTShirtSizeBox
                        hundredTShirtSize={hundredTShirtSize}
                        onEdit={() =>
                            dialogRef.current &&
                            dialogRef.current.openHundredTShirtSizeDialog(
                                {
                                    hundredTShirtSize,
                                    options: resource.hundredTShirtSizeTextOptions,
                                    memberID,
                                },
                                updateHundredTShirtSize,
                            )
                        }
                        className="mb-1"
                    />
                    <SpecialConditionBox
                        specialCondition={specialCondition}
                        onEdit={() =>
                            dialogRef.current &&
                            dialogRef.current.openSpecialConditionDialog(
                                {
                                    specialCondition,
                                    memberID,
                                },
                                updateSpecialCondition,
                            )
                        }
                        className="mb-2"
                    />
                    <MemoBox
                        memo={memo}
                        onEdit={() =>
                            dialogRef.current &&
                            dialogRef.current.openMemoDialog(
                                {
                                    memo,
                                    memberID,
                                },
                                updateMemo,
                            )
                        }
                        className="mb-2"
                    />
                    <BatchPaymentMemoBox
                        batchPaymentMemo={batchPaymentMemo}
                        onEdit={() =>
                            dialogRef.current &&
                            dialogRef.current.openBatchPaymentMemoDialog(
                                {
                                    batchPaymentMemo,
                                    memberID,
                                },
                                updateBatchPaymentMemo,
                            )
                        }
                    />
                </Card.CardContent>
            </Card>
            <TodoCard
                todoRef={todoRef}
                memberID={memberID}
                todos={serviceTasks}
                options={serviceTodoTextOptions}
                onClick={() =>
                    dialogRef.current &&
                    dialogRef.current.openTodoDialog(
                        {
                            todos: (todoRef.current && todoRef.current.getTodos()) || serviceTasks,
                            memberID,
                            options: serviceTodoTextOptions,
                        },
                        updateServiceTasks,
                    )
                }
            />
        </React.Fragment>
    );
};

export default MemberDetailSideBar;
